import type {
  Service,
  ServiceFilters,
} from '@wix/ambassador-bookings-catalog-search-v1-catalog-search/types';
import type {
  FilterBarState,
  RentalFilterBy,
  ResultsAvailability,
} from '../types/types';
import { addCalendarDays, isSingleDay } from './dateRange';

const MINUTES_PER_DAY = 24 * 60;

/**
 * Drops a selection the filter bar can no longer show. With "Filter by:
 * Location" the chips are locations and nothing else renders resource types,
 * so a resource-type selection made before the switch would keep narrowing
 * the query with no way to clear it. Locations need no counterpart: the search
 * bar's location field always shows and clears them.
 */
export function withVisibleChipFilters(
  state: FilterBarState,
  filterBy: RentalFilterBy,
): FilterBarState {
  return filterBy === 'location' && state.resourceTypeIds.length > 0
    ? { ...state, resourceTypeIds: [] }
    : state;
}

/** WQL filter applied to the service catalog itself (not availability). */
export type ServiceQueryFilter = Record<string, unknown>;

/**
 * Catalog-level filter. Rental basis and payment option are properties of the
 * service, so they belong here rather than in the availability filter.
 *
 * Only a single selection narrows the query: picking both hourly *and* daily
 * (or both payment options) is the same as picking neither.
 */
export function buildServiceFilter(
  state: FilterBarState,
  appId: string,
): ServiceQueryFilter {
  const conditions: ServiceQueryFilter = {
    appId,
    hidden: { $ne: true },
  };

  if (state.rentalBasis.length === 1) {
    conditions['schedule.availabilityConstraints.durationRange.unitType'] = {
      $eq: state.rentalBasis[0] === 'hourly' ? 'HOUR' : 'DAY',
    };
  }

  if (state.paymentOptions.length === 1) {
    if (state.paymentOptions.includes('online')) {
      conditions['payment.options.online'] = { $eq: true };
    } else if (state.paymentOptions.includes('inPerson')) {
      conditions['payment.options.inPerson'] = { $eq: true };
    }
  }

  return conditions;
}

function endDateTime(endDate: string, duration: string): string {
  const [hours, minutes] = duration.split(':').map(Number);
  const total = hours * 60 + minutes;
  const days = Math.floor(total / MINUTES_PER_DAY);
  const remainder = total % MINUTES_PER_DAY;
  const time = `${String(Math.floor(remainder / 60)).padStart(2, '0')}:${String(
    remainder % 60,
  ).padStart(2, '0')}`;
  return `${addCalendarDays(endDate, days)}T${time}:00`;
}

export function getLocalDateTimeRange(
  state: FilterBarState,
): [string, string] | null {
  if (!state.dateRange) {
    return null;
  }
  const [startDate, endDate] = state.dateRange;
  const [startTime, duration] = state.timeRange ?? ['00:00', '24:00'];
  return [`${startDate}T${startTime}:00`, endDateTime(endDate, duration)];
}

/**
 * Drives both the query's `exactMatch` and the `searchMode` carried to the
 * rental page — they must agree, or the page pre-fills an unverified range.
 */
export function isExactSearch(
  state: FilterBarState,
  dailySearchResultsAvailability: ResultsAvailability,
): boolean {
  if (!state.dateRange) {
    return false;
  }
  return isSingleDay(state.dateRange)
    ? state.timeRange !== null
    : dailySearchResultsAvailability === 'fullyAvailable';
}

/* The time component is how the page knows the selection carries hours. */
export function getCarriedDateRange(
  state: FilterBarState,
): [string, string] | null {
  if (!state.dateRange) {
    return null;
  }
  if (state.timeRange === null) {
    return state.dateRange;
  }
  const [startDate, endDate] = state.dateRange;
  const [startTime, duration] = state.timeRange;
  return [`${startDate}T${startTime}:00`, endDateTime(endDate, duration)];
}

/**
 * Availability/resource filter. Resource types and attributes are properties of
 * the *resources* a service uses, which is what `serviceFilters` matches on.
 */
export function buildAvailabilityFilter(
  state: FilterBarState,
  dailySearchResultsAvailability: ResultsAvailability = 'someAvailability',
): ServiceFilters | undefined {
  const filter: ServiceFilters = {};

  if (state.locationIds.length > 0) {
    filter.locationIds = state.locationIds;
  }

  const dateTimeRange = getLocalDateTimeRange(state);
  if (dateTimeRange && state.dateRange) {
    [filter.localStartDate, filter.localEndDate] = dateTimeRange;
    filter.exactMatch = isExactSearch(state, dailySearchResultsAvailability);
  }

  if (state.resourceTypeIds.length > 0) {
    filter.resourceTypes = state.resourceTypeIds;
  }

  const attributes: NonNullable<ServiceFilters['attributes']> = [];
  for (const id of state.attributes.booleans) {
    attributes.push({ attributeId: id, boolValue: true });
  }
  for (const [id, enumValue] of Object.entries(state.attributes.enums)) {
    attributes.push({ attributeId: id, enumValue });
  }
  for (const [id, [min, max]] of Object.entries(state.attributes.numbers)) {
    attributes.push({ attributeId: id, numberRangeValue: { min, max } });
  }
  if (attributes.length > 0) {
    filter.attributes = attributes;
  }

  filter.includeUnavailable = state.showUnavailable;

  return filter;
}

export function hasAssignedResource(service: Service | undefined): boolean {
  return (service?.serviceResources?.[0]?.resourceIds?.values ?? []).some(
    (id) => !!id?.trim(),
  );
}

/** Number of filters the user has applied, for the button's count badge. */
export function countActiveFilters(state: FilterBarState): number {
  return (
    state.attributes.booleans.length +
    Object.keys(state.attributes.enums).length +
    Object.keys(state.attributes.numbers).length +
    state.rentalBasis.length +
    state.paymentOptions.length
  );
}
